import { PublicKey } from "@solana/web3.js";

import { fetchPlatformFee } from "./arcaneProgram";
import type { Network } from "./config/constants";
import { getIndexerUrl } from "./config/index";
import { getConfig } from "./config/runtime";

export const QUOTE_REQUEST_TIMEOUT_MS = 3000;

export type QuoteRelayer = {
  url: string;
  [key: string]: unknown;
};

export type QuoteTransaction = Record<string, unknown>;

export type QuoteSplit = {
  denomination: number;
  totalRelayerFee: number;
  relayer: QuoteRelayer;
  transaction: QuoteTransaction;
};

export type Quote = {
  amount: number;
  transactionCount: number;
  recipientAmount: number;
  totalRelayerFee: number;
  etaSeconds: number;
  splits: QuoteSplit[];
  platformFee?: number;
};

export type GetQuoteResponse = {
  success: boolean;
  quote: Quote;
};

export type GetQuoteParams = {
  amount: number | string;
  amountLamports: number | string | bigint;
  recipient: PublicKey | string;
  /** Indexer asset label (default `SOL`). */
  asset?: string;
  /** Cluster for the quote (default active SDK network). */
  network?: Network;
};

function normalizeRecipient(recipient: PublicKey | string): string {
  return recipient instanceof PublicKey
    ? recipient.toBase58()
    : recipient.trim();
}

function serializeAmountLamports(
  amountLamports: number | string | bigint
): number | string {
  return typeof amountLamports === "bigint"
    ? amountLamports.toString()
    : amountLamports;
}

async function fetchPlatformFeePercent(): Promise<number> {
  return parseFloat(await fetchPlatformFee()) / 100;
}

/** Fetch a withdraw quote from the indexer. Requires SDK init or an active instance context. */
export async function getQuote(params: GetQuoteParams): Promise<GetQuoteResponse> {
  const network = params.network ?? getConfig().network;
  const res = await fetch(`${getIndexerUrl()}/quote`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      amount: params.amount,
      amountLamports: serializeAmountLamports(params.amountLamports),
      asset: params.asset ?? "SOL",
      network,
      recipient: normalizeRecipient(params.recipient),
    }),
    signal: AbortSignal.timeout(QUOTE_REQUEST_TIMEOUT_MS),
  });

  if (!res.ok) {
    throw new Error("Failed to fetch quote");
  }

  const data = (await res.json()) as GetQuoteResponse;
  if (!data.success) {
    throw new Error("Quote request was not successful");
  }

  const platformFeePercent = await fetchPlatformFeePercent();
  const platformFee = data.quote.amount * platformFeePercent;

  return {
    ...data,
    quote: {
      ...data.quote,
      recipientAmount: data.quote.recipientAmount - platformFee,
      platformFee,
    },
  };
}
